'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import { useLocale } from '@/lib/i18n/LocaleContext';

// Hover effect for title letters. GSAP is injected so this module doesn't
// pull GSAP into the homepage entry chunk (TASK-4).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyHoverEffect(letters: NodeListOf<Element>, gsap: any) {
  const spansArray = Array.from(letters);

  const handleMouseMove = (e: Event) => {
    const mouseEvent = e as MouseEvent;
    const hoveredSpan = mouseEvent.target as HTMLElement;
    const rect = hoveredSpan.getBoundingClientRect();
    const mouseX = mouseEvent.clientX - rect.left;
    const scaleFactor = 0.2;
    const center = rect.width / 2;

    let scale: number;
    if (mouseX < center) {
      scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
    } else {
      scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
    }

    gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

    const hoveredIndex = spansArray.indexOf(hoveredSpan);
    const prevSpan = spansArray[hoveredIndex - 1];
    const nextSpan = spansArray[hoveredIndex + 1];

    if (prevSpan) {
      const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
      const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
      gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
    }

    if (nextSpan) {
      const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
      const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
      gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
    }
  };

  const handleMouseLeave = () => {
    spansArray.forEach((span) => {
      gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
    });
  };

  spansArray.forEach((span) => {
    span.addEventListener('mousemove', handleMouseMove);
    span.addEventListener('mouseleave', handleMouseLeave);
  });
}

export function OurStudioSection() {
  const { dict, href, dir } = useLocale();
  const isRtl = dir === 'rtl';
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);

  // GSAP dynamic-imported so it doesn't ship in the homepage entry chunk
  // (TASK-4).
  useEffect(() => {
    if (!sectionRef.current || !titleRef.current) return;

    let ctx: { revert: () => void } | undefined;
    let cancelled = false;

    (async () => {
      const { gsap } = await import('@/lib/gsap');
      if (cancelled) return;

      ctx = gsap.context(() => {
      // Animate subtitle
      if (subtitleRef.current) {
        gsap.set(subtitleRef.current, { y: 30, opacity: 0 });
        gsap.to(subtitleRef.current, {
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
          },
          y: 0,
          opacity: 0.5,
          duration: 0.7,
          ease: 'power3.out',
        });
      }

      // Animate title letters
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);

        shuffledLetters.forEach((letter, index) => {
          gsap.to(letter, {
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
            },
            duration: 0.6,
            scaleY: 1,
            opacity: 1,
            ease: 'power3.out',
            delay: index * 0.05,
          });
        });

        // Apply hover effect
        setTimeout(() => {
          applyHoverEffect(letters, gsap);
        }, 1000);
      }
    }, sectionRef);
    })();

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, []);

  const title = dict.ourStudio.title;

  return (
    <section
      ref={sectionRef}
      className="relative py-24 md:min-h-screen md:py-0 flex flex-col items-center justify-center border-t border-white/5"
      style={{ backgroundColor: 'rgba(12,12,12,0.65)' }}
    >
      {/* Subtitle */}
      <p
        ref={subtitleRef}
        className="text-center mb-8"
      >
        <span className="eyebrow">{dict.ourStudio.eyebrow}</span>
      </p>

      {/* Title - OUR STUDIO (Clickable). Splitting into one span per
          character breaks Arabic/Sorani letter joining and reorders the
          isolated boxes under RTL, so translated titles render as a single
          "letter" span instead (the GSAP reveal/hover effect below treats
          any element with the `.letter` class the same way, whole-word or
          per-character). */}
      <Link href={href("/about-datacode")} className="group">
        <h2
          ref={titleRef}
          className="rtl-display-l font-six-caps text-[calc(1rem+20vw)] leading-[0.9] text-white uppercase text-center transition-opacity duration-300 group-hover:opacity-80"
        >
          {(isRtl ? [title] : title.split('')).map((piece, i) => (
            <span
              key={i}
              className={`letter inline-block ${piece === ' ' ? 'mx-4' : ''}`}
              style={{ transformOrigin: 'center bottom' }}
            >
              {piece === ' ' ? '\u00A0' : piece}
            </span>
          ))}
        </h2>
      </Link>
    </section>
  );
}
